/**
 * Admin → Cupones. Campaigns (the rules) and their codes (what buyers type),
 * with every use linked to its order and payment. Opening a campaign keeps the
 * section and adds `?campaign=<id>` to the URL, so a campaign can be linked
 * from Pagos and survives a reload.
 */
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Archive,
  Ban,
  ChevronLeft,
  CircleCheck,
  Copy,
  Download,
  ExternalLink,
  Pause,
  Pencil,
  Play,
  Plus,
  TicketPercent,
} from "lucide-react";
import { cn } from "@hey404/ui";
import { apiFetch, apiUrl, AUTH_TOKEN_KEY } from "./lib/auth";
import {
  AdminHeading,
  adminErrorClass,
  btnAccent,
  btnPrimary,
  btnSecondary,
  fieldErrorClass,
  inputClass,
  labelClass,
} from "./admin-ui";
import { Dialog } from "./dialog";
import { RowMenu } from "./row-menu";
import { Select } from "./select";
import { TableScroll } from "./table-scroll";
import { useToast } from "./toast";
import { AdminSkeleton } from "./loading";

type Currency = "USD" | "ARS" | "COP";
type CampaignStatus = "active" | "paused" | "archived";

type Totals = Array<{
  currency: string;
  paidMinor: number;
  discountMinor: number;
}>;

export type Campaign = {
  id: string;
  name: string;
  notes: string;
  partner: string | null;
  status: CampaignStatus;
  codeMode: "shared" | "unique";
  percentOff: number;
  startsAt: string | null;
  endsAt: string | null;
  maxRedemptions: number | null;
  maxRedemptionsPerUser: number | null;
  minOrderMinor: number | null;
  minOrderCurrency: Currency | null;
  allCourses: boolean;
  courseIds: string[];
  firstPurchaseOnly: boolean;
  systemKey: string | null;
  createdAt: string;
  code: string | null;
  codeCount: number;
  stats: {
    uses: number;
    buyers: number;
    reserved: number;
    overLimit: number;
    totals: Totals;
  };
};

type Redemption = {
  id: string;
  status: "reserved" | "redeemed" | "released";
  overLimit: boolean;
  code: string;
  currency: string;
  subtotalMinor: number;
  discountMinor: number;
  createdAt: string;
  redeemedAt: string | null;
  orderId: string;
  orderStatus: string | null;
  provider: string | null;
  providerPaymentId: string | null;
  userName: string | null;
  userEmail: string | null;
  courses: string[];
};

type Code = {
  id: string;
  code: string;
  maxRedemptions: number | null;
  disabledAt: string | null;
  uses: number;
  reserved: number;
  lastUserEmail: string | null;
};

type CourseOption = { id: string; title: string };

const CAMPAIGN_PARAM = "campaign";

// Spanish copy for every reason the API can refuse a code.
export const couponRejectionLabels: Record<string, string> = {
  not_found: "Ese código no existe.",
  disabled: "El código está desactivado.",
  paused: "La campaña está pausada.",
  not_started: "La campaña todavía no empieza.",
  expired: "La campaña ya venció.",
  used: "Ese código ya se usó.",
  exhausted: "La campaña ya no tiene usos disponibles.",
  user_limit: "Esta persona ya usó la campaña el máximo de veces.",
  first_purchase_only: "Es solo para la primera compra.",
  not_applicable: "No aplica a estos cursos.",
  below_minimum: "No alcanza la compra mínima.",
  below_provider_minimum:
    "El total queda por debajo del mínimo que se puede cobrar.",
};

const saveErrors: Record<string, string> = {
  code_taken: "Ese código ya existe. Elige otro.",
  invalid_window: "La fecha de fin tiene que ser posterior a la de inicio.",
  invalid_minimum: "Para la compra mínima indica monto y moneda.",
  missing_courses: "Elige al menos un curso o aplica la campaña a todos.",
  invalid_courses:
    "Alguno de los cursos ya no existe. Recarga y vuelve a elegir.",
  percent_locked:
    "El descuento no se puede cambiar porque la campaña ya se usó. Crea una campaña nueva.",
  system_campaign:
    "Es una campaña del sistema: solo puedes pausarla o editar sus notas.",
};

const money = (minor: number, currency: string) =>
  new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(minor / 100);

const codeCountLabel = (count: number) =>
  count === 1 ? "1 código único" : `${count} códigos únicos`;

const totalsLabel = (totals: Totals, key: "paidMinor" | "discountMinor") =>
  totals.length
    ? totals.map((row) => money(row[key], row.currency)).join(" · ")
    : "—";

const shortDate = (value: string | Date) =>
  new Date(value).toLocaleDateString("es-AR", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

const dateTime = (value: string) =>
  new Date(value).toLocaleString("es-AR", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });

function windowLabel(campaign: Pick<Campaign, "startsAt" | "endsAt">) {
  const { startsAt, endsAt } = campaign;
  if (startsAt && endsAt)
    return `${shortDate(startsAt)} – ${shortDate(endsAt)}`;
  if (endsAt) return `Hasta el ${shortDate(endsAt)}`;
  if (startsAt) return `Desde el ${shortDate(startsAt)}`;
  return "Sin vencimiento";
}

/** What the team needs to know at a glance, beyond the stored status. */
function displayStatus(campaign: Campaign): {
  label: string;
  tone: string;
} {
  const now = Date.now();
  if (campaign.status === "archived")
    return { label: "Archivada", tone: "bg-bone" };
  if (campaign.status === "paused")
    return { label: "Pausada", tone: "bg-lemon" };
  if (campaign.endsAt && new Date(campaign.endsAt).getTime() <= now)
    return { label: "Vencida", tone: "bg-ink text-paper" };
  if (campaign.startsAt && new Date(campaign.startsAt).getTime() > now)
    return { label: "Programada", tone: "bg-grape" };
  if (
    campaign.maxRedemptions !== null &&
    campaign.stats.uses >= campaign.maxRedemptions
  )
    return { label: "Agotada", tone: "bg-ink text-paper" };
  return { label: "Activa", tone: "bg-acid" };
}

function Chip({
  tone = "bg-bone",
  children,
}: {
  tone?: string;
  children: ReactNode;
}) {
  return (
    <span
      className={`border-ink border-chip inline-block whitespace-nowrap px-2 py-0.5 font-mono text-xs font-bold uppercase ${tone}`}
    >
      {children}
    </span>
  );
}

function StatusChip({ campaign }: { campaign: Campaign }) {
  const status = displayStatus(campaign);
  return <Chip tone={status.tone}>{status.label}</Chip>;
}

function usesLabel(campaign: Campaign) {
  return campaign.maxRedemptions === null
    ? String(campaign.stats.uses)
    : `${campaign.stats.uses} / ${campaign.maxRedemptions}`;
}

function rulesSummary(campaign: Campaign, courseTitle: (id: string) => string) {
  const parts = [
    `${campaign.percentOff}% de descuento`,
    campaign.allCourses
      ? "todos los cursos"
      : campaign.courseIds.map(courseTitle).join(", "),
    campaign.maxRedemptions === null
      ? "usos ilimitados"
      : `hasta ${campaign.maxRedemptions} usos`,
    campaign.maxRedemptionsPerUser === null
      ? "sin límite por persona"
      : `${campaign.maxRedemptionsPerUser} por persona`,
  ];
  if (campaign.minOrderMinor !== null && campaign.minOrderCurrency)
    parts.push(
      `compra mínima ${money(campaign.minOrderMinor, campaign.minOrderCurrency)}`,
    );
  if (campaign.firstPurchaseOnly) parts.push("solo primera compra");
  parts.push(windowLabel(campaign).toLowerCase());
  return parts.join(" · ");
}

function MetricTile({
  label,
  value,
  index,
}: {
  label: string;
  value: ReactNode;
  index: number;
}) {
  return (
    <div
      className={`border-ink shadow-brutal border-surface p-5 transition-[translate] duration-200 hover:-translate-y-1 ${["bg-acid", "bg-bubble", "bg-lemon", "bg-grape", "bg-bone"][index % 5]} ${["rounded-[3px_18px_6px_6px]", "rounded-[16px_4px_14px_3px]", "rounded-[3px_3px_20px_5px]"][index % 3]}`}
    >
      <span className="block font-mono text-xs font-bold uppercase tracking-[0.06em]">
        {label}
      </span>
      <strong className="font-display mt-2 block break-words text-3xl font-extrabold leading-tight">
        {value}
      </strong>
    </div>
  );
}

async function downloadCsv(path: string, onError: () => void) {
  const token = localStorage.getItem(AUTH_TOKEN_KEY);
  const response = await fetch(`${apiUrl}${path}`, {
    credentials: "include",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) return onError();
  const name =
    /filename="([^"]+)"/.exec(
      response.headers.get("Content-Disposition") ?? "",
    )?.[1] ?? "cupones.csv";
  const url = URL.createObjectURL(await response.blob());
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = name;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

const errorCode = (error: unknown) =>
  (error as { body?: { error?: string } }).body?.error ?? "";

/** Opens a campaign (or the list) by URL, the way Pagos links to it. */
export function couponCampaignPath(campaignId: string) {
  return `/admin/coupons?${CAMPAIGN_PARAM}=${encodeURIComponent(campaignId)}`;
}

function currentCampaignId() {
  return new URLSearchParams(window.location.search).get(CAMPAIGN_PARAM);
}

function navigate(url: string) {
  window.history.pushState({}, "", url);
  window.dispatchEvent(new PopStateEvent("popstate"));
  window.scrollTo({ top: 0 });
}

export function CouponsAdmin() {
  const [campaignId, setCampaignId] = useState(currentCampaignId);
  useEffect(() => {
    const sync = () => setCampaignId(currentCampaignId());
    window.addEventListener("popstate", sync);
    return () => window.removeEventListener("popstate", sync);
  }, []);
  const courseOptions = useQuery({
    queryKey: ["admin", "coupons", "course-options"],
    queryFn: () =>
      apiFetch<{ courses: CourseOption[] }>(
        "/api/admin/coupons/course-options",
      ).then((result) => result.courses),
  });
  const courses = courseOptions.data ?? [];
  return campaignId ? (
    <CampaignDetail
      campaignId={campaignId}
      courses={courses}
      onBack={() => navigate("/admin/coupons")}
    />
  ) : (
    <CampaignList
      courses={courses}
      onOpen={(id) => navigate(couponCampaignPath(id))}
    />
  );
}

function CampaignList({
  courses,
  onOpen,
}: {
  courses: CourseOption[];
  onOpen: (id: string) => void;
}) {
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [status, setStatus] = useState("");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Campaign | null>(null);
  const queryClient = useQueryClient();
  const toast = useToast();
  const query = useQuery({
    queryKey: ["admin", "coupons", "list", appliedSearch, status],
    queryFn: () =>
      apiFetch<{
        summary: {
          activeCampaigns: number;
          usesLast30Days: number;
          totals: Totals;
        };
        campaigns: Campaign[];
      }>(
        `/api/admin/coupons?search=${encodeURIComponent(appliedSearch)}&status=${status}`,
      ),
  });
  const setCampaignStatus = async (
    campaign: Campaign,
    next: CampaignStatus,
    message: string,
  ) => {
    try {
      await apiFetch(`/api/admin/coupons/${campaign.id}`, {
        method: "PATCH",
        body: JSON.stringify({ status: next }),
      });
      await queryClient.invalidateQueries({ queryKey: ["admin", "coupons"] });
      toast(message);
    } catch {
      toast("No pudimos cambiar el estado. Intenta de nuevo.", "error");
    }
  };

  if (query.isPending) return <AdminSkeleton variant="commerce" />;
  if (!query.data)
    return (
      <p role="alert" className={adminErrorClass}>
        No pudimos cargar los cupones.
      </p>
    );
  const { summary, campaigns } = query.data;
  return (
    <section>
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <MetricTile
          index={0}
          label="Campañas activas"
          value={summary.activeCampaigns}
        />
        <MetricTile
          index={1}
          label="Usos · 30 días"
          value={summary.usesLast30Days}
        />
        <MetricTile
          index={2}
          label="Vendido con cupón"
          value={totalsLabel(summary.totals, "paidMinor")}
        />
        <MetricTile
          index={3}
          label="Descuento otorgado"
          value={totalsLabel(summary.totals, "discountMinor")}
        />
      </div>

      <div className="mt-8 flex flex-wrap gap-3">
        <form
          onSubmit={(event) => {
            event.preventDefault();
            setAppliedSearch(search.trim());
          }}
          className="flex min-w-[min(100%,18rem)] flex-1 gap-2"
        >
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            aria-label="Buscar campañas por nombre, código o asociado"
            placeholder="Buscar por nombre, código o asociado"
            className={cn(inputClass, "mt-0 min-w-0 flex-1 px-4 py-3")}
          />
          <button className={`${btnSecondary} px-5`}>Buscar</button>
        </form>
        <Select
          value={status}
          onChange={(event) => setStatus(event.target.value)}
          aria-label="Filtrar por estado"
          wrapperClassName="w-52"
        >
          <option value="">Todos los estados</option>
          <option value="active">Activas</option>
          <option value="paused">Pausadas</option>
          <option value="archived">Archivadas</option>
        </Select>
        <button
          type="button"
          onClick={() => setCreating(true)}
          className={`${btnPrimary} gap-2 px-4 py-3 text-sm`}
        >
          <Plus size={17} aria-hidden="true" /> Nueva campaña
        </button>
      </div>

      <article className="mt-6">
        <div className="flex items-center gap-3">
          <h3 className="font-display text-2xl font-extrabold">Campañas</h3>
          <span className="border-ink bg-lemon border-chip rotate-[-2deg] px-2 py-0.5 font-mono text-xs font-bold">
            {campaigns.length}
          </span>
        </div>
        <TableScroll count={campaigns.length} noun="campañas" className="mt-3">
          <table className="data-table" style={{ minWidth: 900 }}>
            <thead>
              <tr>
                <th>Campaña</th>
                <th className="num">Descuento</th>
                <th className="num">Usos</th>
                <th className="num">Personas</th>
                <th className="num">Vendido</th>
                <th>Vigencia</th>
                <th>Estado</th>
                <th aria-label="Acciones" />
              </tr>
            </thead>
            <tbody>
              {campaigns.length === 0 ? (
                <tr>
                  <td colSpan={8} className="empty">
                    {appliedSearch || status
                      ? "Ninguna campaña coincide con la búsqueda."
                      : "Todavía no hay campañas. Crea la primera con «Nueva campaña»."}
                  </td>
                </tr>
              ) : (
                campaigns.map((campaign) => (
                  <tr key={campaign.id}>
                    <td>
                      <button
                        type="button"
                        onClick={() => onOpen(campaign.id)}
                        className="font-display text-left font-extrabold hover:underline"
                      >
                        {campaign.name}
                      </button>
                      <span className="text-ink-soft mt-0.5 block font-mono text-xs">
                        {campaign.codeMode === "shared"
                          ? campaign.code
                          : codeCountLabel(campaign.codeCount)}
                        {campaign.partner ? ` · ${campaign.partner}` : ""}
                      </span>
                    </td>
                    <td className="num font-bold">{campaign.percentOff}%</td>
                    <td className="num">{usesLabel(campaign)}</td>
                    <td className="num">{campaign.stats.buyers}</td>
                    <td className="num">
                      {totalsLabel(campaign.stats.totals, "paidMinor")}
                    </td>
                    <td className="whitespace-nowrap">
                      {windowLabel(campaign)}
                    </td>
                    <td>
                      <StatusChip campaign={campaign} />
                    </td>
                    <td className="text-right">
                      <RowMenu
                        label={`Acciones de ${campaign.name}`}
                        items={[
                          {
                            label: "Ver detalle",
                            icon: TicketPercent,
                            onSelect: () => onOpen(campaign.id),
                          },
                          {
                            label: "Editar",
                            icon: Pencil,
                            onSelect: () => setEditing(campaign),
                          },
                          campaign.status === "active"
                            ? {
                                label: "Pausar",
                                icon: Pause,
                                onSelect: () =>
                                  setCampaignStatus(
                                    campaign,
                                    "paused",
                                    `Pausaste «${campaign.name}».`,
                                  ),
                              }
                            : {
                                label: "Reactivar",
                                icon: Play,
                                onSelect: () =>
                                  setCampaignStatus(
                                    campaign,
                                    "active",
                                    `Reactivaste «${campaign.name}».`,
                                  ),
                              },
                          ...(campaign.status !== "archived" &&
                          !campaign.systemKey
                            ? [
                                {
                                  label: "Archivar",
                                  icon: Archive,
                                  danger: true,
                                  onSelect: () =>
                                    setCampaignStatus(
                                      campaign,
                                      "archived",
                                      `Archivaste «${campaign.name}». Sus códigos ya no funcionan.`,
                                    ),
                                },
                              ]
                            : []),
                        ]}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </TableScroll>
      </article>

      <CodeLookup />

      <CampaignFormDialog
        open={creating}
        courses={courses}
        onClose={() => setCreating(false)}
        onSaved={(campaign) => {
          setCreating(false);
          toast(`Creaste «${campaign.name}».`);
          onOpen(campaign.id);
        }}
      />
      <CampaignFormDialog
        open={editing !== null}
        campaign={editing ?? undefined}
        courses={courses}
        onClose={() => setEditing(null)}
        onSaved={(campaign) => {
          setEditing(null);
          toast(`Guardaste «${campaign.name}».`);
        }}
      />
    </section>
  );
}

/** Support tool: paste a code a buyer sent and see its campaign and state. */
function CodeLookup() {
  const [code, setCode] = useState("");
  const [result, setResult] = useState<
    | {
        found: true;
        campaignId: string;
        campaignName: string;
        code: string;
        usable: boolean;
        reason: string | null;
      }
    | { found: false }
    | null
  >(null);
  const [failed, setFailed] = useState(false);
  return (
    <article className="border-ink bg-bone border-surface shadow-brutal mt-8 p-5">
      <AdminHeading
        title="Revisar un código"
        description="Pega el código que te mandó una persona para ver de qué campaña es y si funciona ahora."
      />
      <form
        className="mt-4 flex flex-wrap gap-2"
        onSubmit={async (event) => {
          event.preventDefault();
          if (!code.trim()) return;
          setFailed(false);
          try {
            setResult(
              await apiFetch(
                `/api/admin/coupons/lookup?code=${encodeURIComponent(code)}`,
              ),
            );
          } catch {
            setFailed(true);
          }
        }}
      >
        <input
          value={code}
          onChange={(event) => setCode(event.target.value)}
          aria-label="Código a revisar"
          placeholder="Ej. BLACKFRIDAY30"
          className={cn(
            inputClass,
            "mt-0 min-w-0 max-w-sm flex-1 font-mono uppercase placeholder:normal-case",
          )}
        />
        <button className={`${btnSecondary} px-5`}>Revisar</button>
      </form>
      {failed && (
        <p role="alert" className={cn(fieldErrorClass, "mt-3")}>
          No pudimos revisar el código. Intenta de nuevo.
        </p>
      )}
      {result && (
        <p className="mt-3 text-sm" role="status">
          {result.found ? (
            <>
              <span className="code font-bold">{result.code}</span> es de{" "}
              <button
                type="button"
                className="font-bold underline"
                onClick={() => navigate(couponCampaignPath(result.campaignId))}
              >
                {result.campaignName}
              </button>
              .{" "}
              {result.usable
                ? "Funciona ahora."
                : (couponRejectionLabels[result.reason ?? ""] ??
                  "No se puede usar ahora.")}
            </>
          ) : (
            couponRejectionLabels.not_found
          )}
        </p>
      )}
    </article>
  );
}

type FormState = {
  name: string;
  notes: string;
  partner: string;
  codeMode: "shared" | "unique";
  code: string;
  prefix: string;
  quantity: string;
  percentOff: string;
  startsAt: string;
  endsAt: string;
  maxRedemptions: string;
  maxRedemptionsPerUser: string;
  minOrder: string;
  minOrderCurrency: Currency;
  allCourses: boolean;
  courseIds: string[];
  firstPurchaseOnly: boolean;
};

// <input type="datetime-local"> works in local time without a zone.
const toLocalInput = (iso: string | null) => {
  if (!iso) return "";
  const date = new Date(iso);
  date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
  return date.toISOString().slice(0, 16);
};
const fromLocalInput = (value: string) =>
  value ? new Date(value).toISOString() : null;
const optionalInt = (value: string) =>
  value.trim() === "" ? null : Math.round(Number(value));

function initialForm(campaign?: Campaign): FormState {
  return {
    name: campaign?.name ?? "",
    notes: campaign?.notes ?? "",
    partner: campaign?.partner ?? "",
    codeMode: campaign?.codeMode ?? "shared",
    code: "",
    prefix: "HEY404",
    quantity: "50",
    percentOff: String(campaign?.percentOff ?? 20),
    startsAt: toLocalInput(campaign?.startsAt ?? null),
    endsAt: toLocalInput(campaign?.endsAt ?? null),
    maxRedemptions:
      campaign?.maxRedemptions != null ? String(campaign.maxRedemptions) : "",
    maxRedemptionsPerUser: campaign
      ? campaign.maxRedemptionsPerUser != null
        ? String(campaign.maxRedemptionsPerUser)
        : ""
      : "1",
    minOrder:
      campaign?.minOrderMinor != null
        ? String(campaign.minOrderMinor / 100)
        : "",
    minOrderCurrency: campaign?.minOrderCurrency ?? "USD",
    allCourses: campaign?.allCourses ?? true,
    courseIds: campaign?.courseIds ?? [],
    firstPurchaseOnly: campaign?.firstPurchaseOnly ?? false,
  };
}

function Field({
  label,
  hint,
  children,
  className,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <label className={cn("block", className)}>
      <span className={labelClass}>{label}</span>
      {children}
      {hint && <span className="text-ink-soft mt-1 block text-xs">{hint}</span>}
    </label>
  );
}

function FormGroup({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <fieldset className="border-ink/15 border-t pt-4 first:border-t-0 first:pt-0">
      <legend className="font-display float-left mb-3 w-full text-lg font-extrabold">
        {title}
      </legend>
      <div className="clear-both grid gap-4 sm:grid-cols-2">{children}</div>
    </fieldset>
  );
}

function CampaignFormDialog({
  open,
  campaign,
  courses,
  onClose,
  onSaved,
}: {
  open: boolean;
  campaign?: Campaign;
  courses: CourseOption[];
  onClose: () => void;
  onSaved: (campaign: Campaign) => void;
}) {
  const editing = campaign !== undefined;
  const [form, setForm] = useState<FormState>(() => initialForm(campaign));
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const queryClient = useQueryClient();
  useEffect(() => {
    if (open) {
      setForm(initialForm(campaign));
      setError("");
    }
  }, [open, campaign]);
  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((current) => ({ ...current, [key]: value }));
  const system = Boolean(campaign?.systemKey);
  const percentLocked =
    editing && campaign.stats.uses + campaign.stats.reserved > 0;
  const lockedRules = system;

  const preview = useMemo(() => {
    const percent = Number(form.percentOff) || 0;
    const scope = form.allCourses
      ? "todos los cursos"
      : form.courseIds.length
        ? courses
            .filter((course) => form.courseIds.includes(course.id))
            .map((course) => course.title)
            .join(", ")
        : "ningún curso todavía";
    const parts = [`${percent}% en ${scope}`];
    parts.push(
      form.maxRedemptions
        ? `hasta ${form.maxRedemptions} usos`
        : "usos ilimitados",
    );
    parts.push(
      form.maxRedemptionsPerUser
        ? `${form.maxRedemptionsPerUser} por persona`
        : "sin límite por persona",
    );
    if (form.minOrder)
      parts.push(
        `compra mínima ${money(Math.round(Number(form.minOrder) * 100), form.minOrderCurrency)}`,
      );
    if (form.firstPurchaseOnly) parts.push("solo primera compra");
    parts.push(
      windowLabel({
        startsAt: fromLocalInput(form.startsAt),
        endsAt: fromLocalInput(form.endsAt),
      }).toLowerCase(),
    );
    return parts.join(" · ");
  }, [form, courses]);

  const submit = async () => {
    setError("");
    const rules = {
      name: form.name.trim(),
      notes: form.notes.trim(),
      partner: form.partner.trim() || null,
      percentOff: Math.round(Number(form.percentOff)),
      startsAt: fromLocalInput(form.startsAt),
      endsAt: fromLocalInput(form.endsAt),
      maxRedemptions: optionalInt(form.maxRedemptions),
      maxRedemptionsPerUser: optionalInt(form.maxRedemptionsPerUser),
      minOrderMinor: form.minOrder
        ? Math.round(Number(form.minOrder) * 100)
        : null,
      minOrderCurrency: form.minOrder ? form.minOrderCurrency : null,
      allCourses: form.allCourses,
      courseIds: form.allCourses ? [] : form.courseIds,
      firstPurchaseOnly: form.firstPurchaseOnly,
    };
    if (rules.name.length < 2) return setError("Ponle un nombre a la campaña.");
    if (!(rules.percentOff >= 1 && rules.percentOff <= 100))
      return setError("El descuento tiene que ser entre 1% y 100%.");
    let body: object;
    if (system) body = { notes: rules.notes };
    else if (editing)
      body = percentLocked ? { ...rules, percentOff: undefined } : rules;
    else
      body =
        form.codeMode === "shared"
          ? {
              ...rules,
              codeMode: "shared",
              ...(form.code.trim() ? { code: form.code.trim() } : {}),
            }
          : {
              ...rules,
              codeMode: "unique",
              prefix: form.prefix.trim(),
              quantity: Math.round(Number(form.quantity)),
            };
    setSaving(true);
    try {
      const result = await apiFetch<{ campaign: Campaign }>(
        editing ? `/api/admin/coupons/${campaign.id}` : "/api/admin/coupons",
        { method: editing ? "PATCH" : "POST", body: JSON.stringify(body) },
      );
      await queryClient.invalidateQueries({ queryKey: ["admin", "coupons"] });
      onSaved(result.campaign);
    } catch (caught) {
      setError(
        saveErrors[errorCode(caught)] ??
          "No pudimos guardar la campaña. Revisa los datos e intenta de nuevo.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      kicker="Cupones"
      title={editing ? `Editar «${campaign.name}»` : "Nueva campaña"}
      icon={TicketPercent}
      tone="acid"
      size="lg"
      description={
        system
          ? saveErrors.system_campaign
          : "El descuento se aplica en la plataforma antes del pago, igual para Stripe y Mercado Pago."
      }
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className={`${btnSecondary} px-4 py-2.5 text-sm`}
          >
            Cancelar
          </button>
          <button
            type="submit"
            form="coupon-campaign-form"
            disabled={saving}
            className={`${btnAccent} px-4 py-2.5 text-sm`}
          >
            {saving
              ? "Guardando…"
              : editing
                ? "Guardar cambios"
                : "Crear campaña"}
          </button>
        </>
      }
    >
      <form
        id="coupon-campaign-form"
        className="space-y-6"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <FormGroup title="Campaña">
          <Field label="Nombre">
            <input
              required
              disabled={lockedRules}
              value={form.name}
              onChange={(event) => set("name", event.target.value)}
              placeholder="Black Friday 2026"
              className={inputClass}
            />
          </Field>
          <Field
            label="Asociado (opcional)"
            hint="A quién se le atribuye: un influencer, una agencia…"
          >
            <input
              disabled={lockedRules}
              value={form.partner}
              onChange={(event) => set("partner", event.target.value)}
              className={inputClass}
            />
          </Field>
          <Field label="Notas internas" className="sm:col-span-2">
            <textarea
              rows={2}
              value={form.notes}
              onChange={(event) => set("notes", event.target.value)}
              className={inputClass}
            />
          </Field>
        </FormGroup>

        {!editing && (
          <FormGroup title="Códigos">
            <div
              role="radiogroup"
              aria-label="Tipo de código"
              className="grid gap-3 sm:col-span-2 sm:grid-cols-2"
            >
              {(
                [
                  [
                    "shared",
                    "Un código compartido",
                    "Lo usa mucha gente, como BLACKFRIDAY30.",
                  ],
                  [
                    "unique",
                    "Códigos únicos",
                    "Uno por persona, de un solo uso. Se generan en lote.",
                  ],
                ] as const
              ).map(([value, title, detail]) => (
                <label
                  key={value}
                  className={cn(
                    "border-ink border-chip rounded-control flex cursor-pointer gap-3 p-3",
                    form.codeMode === value ? "bg-lemon" : "bg-paper",
                  )}
                >
                  <input
                    type="radio"
                    name="codeMode"
                    checked={form.codeMode === value}
                    onChange={() => set("codeMode", value)}
                    className="mt-1"
                  />
                  <span>
                    <span className="block font-bold">{title}</span>
                    <span className="text-ink-soft text-sm">{detail}</span>
                  </span>
                </label>
              ))}
            </div>
            {form.codeMode === "shared" ? (
              <Field
                label="Código"
                hint="Letras, números y guiones. Déjalo vacío para generar uno."
                className="sm:col-span-2"
              >
                <input
                  value={form.code}
                  onChange={(event) =>
                    set("code", event.target.value.toUpperCase())
                  }
                  placeholder="BLACKFRIDAY30"
                  maxLength={40}
                  className={cn(
                    inputClass,
                    "font-mono uppercase placeholder:normal-case",
                  )}
                />
              </Field>
            ) : (
              <>
                <Field label="Prefijo" hint="Quedan como PREFIJO-K7M2QX.">
                  <input
                    required
                    value={form.prefix}
                    onChange={(event) =>
                      set("prefix", event.target.value.toUpperCase())
                    }
                    maxLength={20}
                    className={cn(
                      inputClass,
                      "font-mono uppercase placeholder:normal-case",
                    )}
                  />
                </Field>
                <Field label="Cantidad" hint="Hasta 5.000 por vez.">
                  <input
                    required
                    type="number"
                    min={1}
                    max={5000}
                    value={form.quantity}
                    onChange={(event) => set("quantity", event.target.value)}
                    className={inputClass}
                  />
                </Field>
              </>
            )}
          </FormGroup>
        )}

        <FormGroup title="Descuento y vigencia">
          <Field
            label="Descuento (%)"
            hint={
              percentLocked && !system
                ? "Ya se usó: el descuento no se puede cambiar."
                : "100% entrega el curso gratis, sin pasar por el pago."
            }
          >
            <input
              required
              type="number"
              min={1}
              max={100}
              disabled={lockedRules || percentLocked}
              value={form.percentOff}
              onChange={(event) => set("percentOff", event.target.value)}
              className={inputClass}
            />
          </Field>
          <div className="hidden sm:block" />
          <Field label="Desde (opcional)">
            <input
              type="datetime-local"
              disabled={lockedRules}
              value={form.startsAt}
              onChange={(event) => set("startsAt", event.target.value)}
              className={inputClass}
            />
          </Field>
          <Field label="Hasta (opcional)">
            <input
              type="datetime-local"
              disabled={lockedRules}
              value={form.endsAt}
              onChange={(event) => set("endsAt", event.target.value)}
              className={inputClass}
            />
          </Field>
        </FormGroup>

        <FormGroup title="Límites">
          <Field label="Usos en total" hint="Vacío = ilimitados.">
            <input
              type="number"
              min={1}
              disabled={lockedRules}
              value={form.maxRedemptions}
              onChange={(event) => set("maxRedemptions", event.target.value)}
              className={inputClass}
            />
          </Field>
          <Field label="Usos por persona" hint="Vacío = sin límite.">
            <input
              type="number"
              min={1}
              disabled={lockedRules}
              value={form.maxRedemptionsPerUser}
              onChange={(event) =>
                set("maxRedemptionsPerUser", event.target.value)
              }
              className={inputClass}
            />
          </Field>
        </FormGroup>

        <FormGroup title="Condiciones">
          <Field
            label="Compra mínima (opcional)"
            hint="Suma de los cursos con descuento. Solo aplica a compras en esa moneda."
          >
            <div className="flex gap-2">
              <input
                type="number"
                min={0}
                step="0.01"
                disabled={lockedRules}
                value={form.minOrder}
                onChange={(event) => set("minOrder", event.target.value)}
                className={cn(inputClass, "flex-1")}
              />
              <Select
                value={form.minOrderCurrency}
                disabled={lockedRules}
                onChange={(event) =>
                  set("minOrderCurrency", event.target.value as Currency)
                }
                aria-label="Moneda de la compra mínima"
                wrapperClassName="mt-1 w-28"
              >
                <option value="USD">USD</option>
                <option value="ARS">ARS</option>
                <option value="COP">COP</option>
              </Select>
            </div>
          </Field>
          <label className="flex items-start gap-3 self-center">
            <input
              type="checkbox"
              disabled={lockedRules}
              checked={form.firstPurchaseOnly}
              onChange={(event) =>
                set("firstPurchaseOnly", event.target.checked)
              }
              className="mt-1"
            />
            <span>
              <span className="block font-bold">Solo primera compra</span>
              <span className="text-ink-soft text-sm">
                Para personas que nunca compraron un curso.
              </span>
            </span>
          </label>
          <div className="sm:col-span-2">
            <span className={labelClass}>Cursos</span>
            <div className="mt-2 flex flex-wrap gap-2">
              {[
                [true, "Todos los cursos"],
                [false, "Elegir cursos"],
              ].map(([value, label]) => (
                <button
                  key={String(value)}
                  type="button"
                  disabled={lockedRules}
                  aria-pressed={form.allCourses === value}
                  onClick={() => set("allCourses", value as boolean)}
                  className={cn(
                    "border-ink border-chip rounded-control min-h-10 px-3 text-sm",
                    form.allCourses === value
                      ? "bg-ink text-paper"
                      : "bg-paper hover:bg-paper-dim",
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
            {!form.allCourses && (
              <ul className="border-ink border-chip rounded-control divide-ink/15 mt-3 max-h-56 divide-y overflow-y-auto">
                {courses.map((course) => (
                  <li key={course.id}>
                    <label className="flex cursor-pointer items-center gap-3 px-3 py-2.5 text-sm">
                      <input
                        type="checkbox"
                        disabled={lockedRules}
                        checked={form.courseIds.includes(course.id)}
                        onChange={(event) =>
                          set(
                            "courseIds",
                            event.target.checked
                              ? [...form.courseIds, course.id]
                              : form.courseIds.filter((id) => id !== course.id),
                          )
                        }
                      />
                      {course.title}
                    </label>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </FormGroup>

        <p className="border-ink bg-bone border-chip rounded-control p-3 text-sm">
          <span className="font-mono text-xs font-bold uppercase">
            Resumen ·{" "}
          </span>
          {preview}
        </p>
        {error && (
          <p role="alert" className={fieldErrorClass}>
            {error}
          </p>
        )}
      </form>
    </Dialog>
  );
}

function CampaignDetail({
  campaignId,
  courses,
  onBack,
}: {
  campaignId: string;
  courses: CourseOption[];
  onBack: () => void;
}) {
  const [tab, setTab] = useState<"uses" | "codes">("uses");
  const [editing, setEditing] = useState(false);
  const queryClient = useQueryClient();
  const toast = useToast();
  const query = useQuery({
    queryKey: ["admin", "coupons", "campaign", campaignId],
    queryFn: () =>
      apiFetch<{ campaign: Campaign }>(
        `/api/admin/coupons/${encodeURIComponent(campaignId)}`,
      ).then((result) => result.campaign),
  });
  const courseTitle = (id: string) =>
    courses.find((course) => course.id === id)?.title ?? "Curso";
  const back = (
    <button
      type="button"
      onClick={onBack}
      className="font-display flex items-center gap-1.5 text-sm font-bold hover:underline"
    >
      <ChevronLeft size={16} aria-hidden="true" /> Volver a cupones
    </button>
  );
  if (query.isPending) return <AdminSkeleton variant="commerce" />;
  if (!query.data)
    return (
      <section className="space-y-4">
        {back}
        <p role="alert" className={adminErrorClass}>
          No encontramos esta campaña.
        </p>
      </section>
    );
  const campaign = query.data;
  const setStatus = async (next: CampaignStatus, message: string) => {
    try {
      await apiFetch(`/api/admin/coupons/${campaign.id}`, {
        method: "PATCH",
        body: JSON.stringify({ status: next }),
      });
      await queryClient.invalidateQueries({ queryKey: ["admin", "coupons"] });
      toast(message);
    } catch {
      toast("No pudimos cambiar el estado. Intenta de nuevo.", "error");
    }
  };
  const exportCsv = (kind: "codes" | "redemptions") =>
    downloadCsv(`/api/admin/coupons/${campaign.id}/export?kind=${kind}`, () =>
      toast("No pudimos descargar el archivo.", "error"),
    );

  return (
    <section className="space-y-6">
      {back}
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="font-display text-3xl font-extrabold leading-tight">
              {campaign.name}
            </h2>
            <StatusChip campaign={campaign} />
            {campaign.systemKey && <Chip>Del sistema</Chip>}
          </div>
          <p className="text-ink-soft mt-1 font-mono text-sm">
            {campaign.codeMode === "shared"
              ? campaign.code
              : codeCountLabel(campaign.codeCount)}
            {campaign.partner ? ` · ${campaign.partner}` : ""}
          </p>
          <p className="mt-2 max-w-3xl text-sm">
            {rulesSummary(campaign, courseTitle)}
          </p>
          {campaign.notes && (
            <p className="text-ink-soft mt-1 max-w-3xl text-sm italic">
              {campaign.notes}
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setEditing(true)}
            className={`${btnSecondary} gap-2 px-4 py-2.5 text-sm`}
          >
            <Pencil size={15} aria-hidden="true" /> Editar
          </button>
          {campaign.status === "active" ? (
            <button
              type="button"
              onClick={() =>
                setStatus("paused", `Pausaste «${campaign.name}».`)
              }
              className={`${btnSecondary} gap-2 px-4 py-2.5 text-sm`}
            >
              <Pause size={15} aria-hidden="true" /> Pausar
            </button>
          ) : (
            <button
              type="button"
              onClick={() =>
                setStatus("active", `Reactivaste «${campaign.name}».`)
              }
              className={`${btnAccent} gap-2 px-4 py-2.5 text-sm`}
            >
              <Play size={15} aria-hidden="true" /> Reactivar
            </button>
          )}
          <RowMenu
            label={`Más acciones de ${campaign.name}`}
            items={[
              {
                label: "Exportar usos (CSV)",
                icon: Download,
                onSelect: () => void exportCsv("redemptions"),
              },
              {
                label: "Exportar códigos (CSV)",
                icon: Download,
                onSelect: () => void exportCsv("codes"),
              },
              ...(campaign.status !== "archived" && !campaign.systemKey
                ? [
                    {
                      label: "Archivar",
                      icon: Archive,
                      danger: true,
                      onSelect: () =>
                        setStatus(
                          "archived",
                          `Archivaste «${campaign.name}». Sus códigos ya no funcionan.`,
                        ),
                    },
                  ]
                : []),
            ]}
          />
        </div>
      </header>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <MetricTile index={0} label="Usos" value={usesLabel(campaign)} />
        <MetricTile index={1} label="Personas" value={campaign.stats.buyers} />
        <MetricTile
          index={2}
          label="Vendido"
          value={totalsLabel(campaign.stats.totals, "paidMinor")}
        />
        <MetricTile
          index={3}
          label="Descuento otorgado"
          value={totalsLabel(campaign.stats.totals, "discountMinor")}
        />
      </div>
      {(campaign.stats.reserved > 0 || campaign.stats.overLimit > 0) && (
        <p className="border-ink bg-lemon border-chip p-3 text-sm">
          {campaign.stats.reserved > 0 &&
            `${campaign.stats.reserved} ${campaign.stats.reserved === 1 ? "persona está" : "personas están"} pagando ahora con esta campaña (el uso se libera si no paga en 30 minutos). `}
          {campaign.stats.overLimit > 0 &&
            `${campaign.stats.overLimit} ${campaign.stats.overLimit === 1 ? "pago llegó" : "pagos llegaron"} cuando la campaña ya estaba agotada; se respetó el descuento.`}
        </p>
      )}

      <div
        role="tablist"
        aria-label="Detalle de la campaña"
        className="border-ink bg-paper border-chip rounded-control inline-flex gap-1 p-1"
      >
        {(
          [
            ["uses", "Usos"],
            ["codes", "Códigos"],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            role="tab"
            id={`coupon-tab-${id}`}
            aria-selected={tab === id}
            aria-controls="coupon-panel"
            tabIndex={tab === id ? 0 : -1}
            onClick={() => setTab(id)}
            onKeyDown={(event) => {
              if (event.key !== "ArrowRight" && event.key !== "ArrowLeft")
                return;
              event.preventDefault();
              const next = tab === "uses" ? "codes" : "uses";
              setTab(next);
              document.getElementById(`coupon-tab-${next}`)?.focus();
            }}
            className={cn(
              "flex min-h-10 cursor-pointer items-center rounded-[6px] px-4 text-sm transition-colors",
              tab === id ? "bg-ink text-paper" : "hover:bg-paper-dim",
            )}
          >
            {label}
          </button>
        ))}
      </div>
      <div
        id="coupon-panel"
        role="tabpanel"
        aria-labelledby={`coupon-tab-${tab}`}
      >
        {tab === "uses" ? (
          <RedemptionsTable campaignId={campaign.id} />
        ) : (
          <CodesTable campaign={campaign} />
        )}
      </div>

      <CampaignFormDialog
        open={editing}
        campaign={campaign}
        courses={courses}
        onClose={() => setEditing(false)}
        onSaved={(saved) => {
          setEditing(false);
          toast(`Guardaste «${saved.name}».`);
        }}
      />
    </section>
  );
}

const redemptionStatus: Record<
  Redemption["status"],
  { label: string; tone: string }
> = {
  redeemed: { label: "Usado", tone: "bg-acid" },
  reserved: { label: "Pagando", tone: "bg-lemon" },
  released: { label: "Liberado", tone: "bg-bone" },
};

/** Where the team can open the payment itself. */
function PaymentCell({ row }: { row: Redemption }) {
  if (row.provider === "free") return <span>Sin cobro (100%)</span>;
  if (!row.providerPaymentId)
    return <span className="text-ink-soft">Sin pago</span>;
  if (row.provider === "stripe" && /^(pi|cs)_/.test(row.providerPaymentId))
    return (
      <a
        href={`https://dashboard.stripe.com/${row.providerPaymentId.startsWith("pi_") ? "payments" : "checkout/sessions"}/${row.providerPaymentId}`}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-1 font-bold underline"
      >
        Ver en Stripe <ExternalLink size={13} aria-hidden="true" />
      </a>
    );
  return (
    <span className="code" title={row.provider ?? undefined}>
      {row.providerPaymentId}
    </span>
  );
}

function RedemptionsTable({ campaignId }: { campaignId: string }) {
  const [pages, setPages] = useState(1);
  const query = useQuery({
    queryKey: ["admin", "coupons", "redemptions", campaignId, pages],
    queryFn: async () => {
      const rows: Redemption[] = [];
      let hasMore = false;
      for (let page = 0; page < pages; page += 1) {
        const result = await apiFetch<{
          redemptions: Redemption[];
          hasMore: boolean;
        }>(
          `/api/admin/coupons/${encodeURIComponent(campaignId)}/redemptions?offset=${page * 50}`,
        );
        rows.push(...result.redemptions);
        hasMore = result.hasMore;
        if (!hasMore) break;
      }
      return { rows, hasMore };
    },
  });
  if (query.isPending)
    return <p className="text-ink-soft font-mono text-sm">Cargando usos…</p>;
  if (!query.data)
    return (
      <p role="alert" className={adminErrorClass}>
        No pudimos cargar los usos.
      </p>
    );
  const { rows, hasMore } = query.data;
  return (
    <>
      <TableScroll count={rows.length} noun="usos">
        <table className="data-table" style={{ minWidth: 980 }}>
          <thead>
            <tr>
              <th>Fecha</th>
              <th>Persona</th>
              <th>Cursos</th>
              <th>Código</th>
              <th className="num">Descuento</th>
              <th className="num">Pagó</th>
              <th>Estado</th>
              <th>Pago</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={8} className="empty">
                  Nadie usó esta campaña todavía.
                </td>
              </tr>
            ) : (
              rows.map((row) => {
                const status = redemptionStatus[row.status];
                return (
                  <tr key={row.id}>
                    <td className="whitespace-nowrap">
                      {dateTime(row.redeemedAt ?? row.createdAt)}
                    </td>
                    <td>
                      <span className="person-name block">
                        {row.userName ?? "Sin nombre"}
                      </span>
                      <em className="text-ink-soft text-xs">{row.userEmail}</em>
                    </td>
                    <td className="max-w-56">{row.courses.join(", ")}</td>
                    <td className="code">{row.code}</td>
                    <td className="num">
                      {money(row.discountMinor, row.currency)}
                    </td>
                    <td className="num font-bold">
                      {row.status === "redeemed"
                        ? money(
                            row.subtotalMinor - row.discountMinor,
                            row.currency,
                          )
                        : "—"}
                    </td>
                    <td>
                      <span className="flex flex-wrap gap-1">
                        <Chip tone={status.tone}>{status.label}</Chip>
                        {row.overLimit && (
                          <Chip tone="bg-ink text-paper">Fuera de límite</Chip>
                        )}
                      </span>
                    </td>
                    <td>
                      <PaymentCell row={row} />
                      <span className="text-ink-soft block font-mono text-xs">
                        Orden {row.orderId.slice(0, 8)}
                      </span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </TableScroll>
      {hasMore && (
        <button
          type="button"
          onClick={() => setPages((value) => value + 1)}
          className={`${btnSecondary} mt-4 px-4 py-2.5 text-sm`}
        >
          Cargar más
        </button>
      )}
    </>
  );
}

function codeState(code: Code): { label: string; tone: string } {
  if (code.disabledAt)
    return { label: "Desactivado", tone: "bg-ink text-paper" };
  if (code.maxRedemptions !== null && code.uses >= code.maxRedemptions)
    return { label: "Usado", tone: "bg-acid" };
  if (code.reserved > 0) return { label: "Pagando", tone: "bg-lemon" };
  return { label: "Disponible", tone: "bg-bone" };
}

function CodesTable({ campaign }: { campaign: Campaign }) {
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [pages, setPages] = useState(1);
  const [generating, setGenerating] = useState(false);
  const queryClient = useQueryClient();
  const toast = useToast();
  const query = useQuery({
    queryKey: ["admin", "coupons", "codes", campaign.id, appliedSearch, pages],
    queryFn: async () => {
      const rows: Code[] = [];
      let hasMore = false;
      for (let page = 0; page < pages; page += 1) {
        const result = await apiFetch<{ codes: Code[]; hasMore: boolean }>(
          `/api/admin/coupons/${encodeURIComponent(campaign.id)}/codes?offset=${page * 50}&search=${encodeURIComponent(appliedSearch)}`,
        );
        rows.push(...result.codes);
        hasMore = result.hasMore;
        if (!hasMore) break;
      }
      return { rows, hasMore };
    },
  });
  const toggle = async (code: Code) => {
    try {
      await apiFetch(`/api/admin/coupons/${campaign.id}/codes/${code.id}`, {
        method: "PATCH",
        body: JSON.stringify({ disabled: !code.disabledAt }),
      });
      await queryClient.invalidateQueries({
        queryKey: ["admin", "coupons", "codes", campaign.id],
      });
      toast(
        code.disabledAt
          ? `Activaste ${code.code}.`
          : `Desactivaste ${code.code}. Ya no se puede usar.`,
      );
    } catch {
      toast("No pudimos cambiar el código. Intenta de nuevo.", "error");
    }
  };
  const copy = async (text: string, message: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast(message);
    } catch {
      toast("No pudimos copiar. Selecciona el texto a mano.", "error");
    }
  };
  const canGenerate = campaign.codeMode === "unique" && !campaign.systemKey;
  return (
    <>
      <div className="mb-4 flex flex-wrap gap-3">
        <form
          onSubmit={(event) => {
            event.preventDefault();
            setPages(1);
            setAppliedSearch(search.trim());
          }}
          className="flex min-w-[min(100%,16rem)] flex-1 gap-2"
        >
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            aria-label="Buscar un código de la campaña"
            placeholder="Buscar código"
            className={cn(
              inputClass,
              "mt-0 min-w-0 flex-1 px-4 py-3 font-mono uppercase placeholder:normal-case",
            )}
          />
          <button className={`${btnSecondary} px-5`}>Buscar</button>
        </form>
        {canGenerate && (
          <button
            type="button"
            onClick={() => setGenerating(true)}
            className={`${btnPrimary} gap-2 px-4 py-3 text-sm`}
          >
            <Plus size={17} aria-hidden="true" /> Generar más códigos
          </button>
        )}
        {query.data && query.data.rows.length > 0 && (
          <button
            type="button"
            onClick={() =>
              copy(
                query.data.rows
                  .filter((code) => codeState(code).label === "Disponible")
                  .map((code) => code.code)
                  .join("\n"),
                "Copiaste los códigos disponibles de esta lista.",
              )
            }
            className={`${btnSecondary} gap-2 px-4 py-3 text-sm`}
          >
            <Copy size={16} aria-hidden="true" /> Copiar disponibles
          </button>
        )}
      </div>
      {query.isPending ? (
        <p className="text-ink-soft font-mono text-sm">Cargando códigos…</p>
      ) : !query.data ? (
        <p role="alert" className={adminErrorClass}>
          No pudimos cargar los códigos.
        </p>
      ) : (
        <>
          <TableScroll count={query.data.rows.length} noun="códigos">
            <table className="data-table" style={{ minWidth: 640 }}>
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Estado</th>
                  <th className="num">Usos</th>
                  <th>Último uso</th>
                  <th aria-label="Acciones" />
                </tr>
              </thead>
              <tbody>
                {query.data.rows.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="empty">
                      {appliedSearch
                        ? "Ningún código coincide."
                        : "Esta campaña no tiene códigos."}
                    </td>
                  </tr>
                ) : (
                  query.data.rows.map((code) => {
                    const state = codeState(code);
                    return (
                      <tr key={code.id}>
                        <td className="code font-bold">{code.code}</td>
                        <td>
                          <Chip tone={state.tone}>{state.label}</Chip>
                        </td>
                        <td className="num">
                          {code.maxRedemptions === null
                            ? code.uses
                            : `${code.uses} / ${code.maxRedemptions}`}
                        </td>
                        <td>
                          <em className="text-ink-soft text-sm">
                            {code.lastUserEmail ?? "—"}
                          </em>
                        </td>
                        <td className="text-right">
                          <RowMenu
                            label={`Acciones de ${code.code}`}
                            items={[
                              {
                                label: "Copiar código",
                                icon: Copy,
                                onSelect: () =>
                                  void copy(
                                    code.code,
                                    `Copiaste ${code.code}.`,
                                  ),
                              },
                              code.disabledAt
                                ? {
                                    label: "Activar",
                                    icon: CircleCheck,
                                    onSelect: () => void toggle(code),
                                  }
                                : {
                                    label: "Desactivar",
                                    icon: Ban,
                                    danger: true,
                                    onSelect: () => void toggle(code),
                                  },
                            ]}
                          />
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </TableScroll>
          {query.data.hasMore && (
            <button
              type="button"
              onClick={() => setPages((value) => value + 1)}
              className={`${btnSecondary} mt-4 px-4 py-2.5 text-sm`}
            >
              Cargar más
            </button>
          )}
        </>
      )}
      <GenerateCodesDialog
        open={generating}
        campaign={campaign}
        onClose={() => setGenerating(false)}
        onDone={(quantity) => {
          setGenerating(false);
          toast(`Generaste ${quantity} códigos nuevos.`);
        }}
      />
    </>
  );
}

function GenerateCodesDialog({
  open,
  campaign,
  onClose,
  onDone,
}: {
  open: boolean;
  campaign: Campaign;
  onClose: () => void;
  onDone: (quantity: number) => void;
}) {
  const [prefix, setPrefix] = useState("HEY404");
  const [quantity, setQuantity] = useState("50");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const queryClient = useQueryClient();
  return (
    <Dialog
      open={open}
      onClose={onClose}
      kicker={campaign.name}
      title="Generar más códigos"
      icon={TicketPercent}
      tone="acid"
      description={`Códigos de un solo uso con el ${campaign.percentOff}% y las mismas reglas de la campaña.`}
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className={`${btnSecondary} px-4 py-2.5 text-sm`}
          >
            Cancelar
          </button>
          <button
            type="submit"
            form="coupon-generate-form"
            disabled={saving}
            className={`${btnAccent} px-4 py-2.5 text-sm`}
          >
            {saving ? "Generando…" : "Generar"}
          </button>
        </>
      }
    >
      <form
        id="coupon-generate-form"
        className="grid gap-4 sm:grid-cols-2"
        onSubmit={async (event) => {
          event.preventDefault();
          setError("");
          setSaving(true);
          const amount = Math.round(Number(quantity));
          try {
            await apiFetch(`/api/admin/coupons/${campaign.id}/codes`, {
              method: "POST",
              body: JSON.stringify({ prefix: prefix.trim(), quantity: amount }),
            });
            await queryClient.invalidateQueries({
              queryKey: ["admin", "coupons"],
            });
            onDone(amount);
          } catch {
            setError(
              "No pudimos generar los códigos. El prefijo admite letras y números; la cantidad, hasta 5.000.",
            );
          } finally {
            setSaving(false);
          }
        }}
      >
        <Field label="Prefijo">
          <input
            required
            value={prefix}
            onChange={(event) => setPrefix(event.target.value.toUpperCase())}
            maxLength={20}
            className={cn(
              inputClass,
              "font-mono uppercase placeholder:normal-case",
            )}
          />
        </Field>
        <Field label="Cantidad">
          <input
            required
            type="number"
            min={1}
            max={5000}
            value={quantity}
            onChange={(event) => setQuantity(event.target.value)}
            className={inputClass}
          />
        </Field>
        {error && (
          <p role="alert" className={cn(fieldErrorClass, "sm:col-span-2")}>
            {error}
          </p>
        )}
      </form>
    </Dialog>
  );
}
